import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { moneyAccountFormSchema, MoneyAccountFormSchemaValues } from 'src/features/moneyAccounts/utils/formSchema';
import { MoneyAccount } from 'src/features/moneyAccounts/utils/types';
import { useGetMoneyAccount } from 'src/features/moneyAccounts/api/useGetMoneyAccount';
import MoneyAccountForm from 'src/features/moneyAccounts/components/forms/MoneyAccountForm';
import { useParams } from 'react-router-dom';

interface MoneyAccountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMoneyAccount?: MoneyAccount;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: MoneyAccountFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function MoneyAccountFormModal<TData>({
    selectedMoneyAccount,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: MoneyAccountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { entityId = '' } = useParams();

    const { data: moneyAccount } = useGetMoneyAccount({
        id: selectedMoneyAccount?.id ?? '',
        config: { enabled: !!selectedMoneyAccount?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: moneyAccount?.name ?? undefined,
            type: moneyAccount?.type ?? undefined,
            reference: moneyAccount?.reference ?? undefined,
            currency: moneyAccount?.currency ?? undefined,
            is_active: moneyAccount?.is_active ?? undefined,
            iban: moneyAccount?.iban ?? undefined,
            billing_entity: moneyAccount?.billing_entity?.id ?? entityId ?? undefined,
        };
    }, [moneyAccount, entityId]);

    const form = useForm<MoneyAccountFormSchemaValues>({
        resolver: zodResolver(moneyAccountFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [moneyAccount, isModalOpen]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MoneyAccountFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <MoneyAccountForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedMoneyAccount && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default MoneyAccountFormModal;
